import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Check, Save } from "lucide-react";
import { useUpdateAgent } from "@/hooks/useAgentDetail";
import type { AgentDetail } from "@/services/aiAgentsService";

interface ConfigurationTabProps {
  agent: AgentDetail;
  agentId: number;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy} aria-label="Copy to clipboard">
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
    </Button>
  );
}

export function ConfigurationTab({ agent, agentId }: ConfigurationTabProps) {
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description ?? "");

  // Reset form when the viewed agent changes
  useEffect(() => {
    setName(agent.name);
    setDescription(agent.description ?? "");
  }, [agent.id]);
  const updateMutation = useUpdateAgent(agentId);

  const hasChanges = name !== agent.name || description !== (agent.description ?? "");

  const handleSave = () => {
    const data: { name?: string; description?: string } = {};
    if (name !== agent.name) data.name = name;
    if (description !== (agent.description ?? "")) data.description = description;
    updateMutation.mutate(data);
  };

  return (
    <div className="space-y-6 pt-6">
      {/* Agent Identity */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Agent Identity</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Provider</dt>
              <dd className="font-medium">{agent.model_provider ?? "Unknown"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Model</dt>
              <dd className="font-medium">{agent.model_name}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Trace Source</dt>
              <dd className="font-medium">{agent.trace_source}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">OTel Scope</dt>
              <dd className="font-medium">{agent.otel_scope_name ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Fingerprint</dt>
              <dd className="font-mono text-xs flex items-center gap-1">
                <span className="truncate max-w-[300px]">{agent.agent_fingerprint}</span>
                <CopyButton text={agent.agent_fingerprint} />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="font-medium">{new Date(agent.created_at).toLocaleString()}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Agent Description */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Agent Description</CardTitle>
        </CardHeader>
        <CardContent>
          {agent.system_prompt_preview ? (
            <pre className="max-h-60 overflow-y-auto whitespace-pre-wrap rounded-[10px] bg-muted p-4 font-mono text-sm">
              {agent.system_prompt_preview}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground italic">No description available</p>
          )}
        </CardContent>
      </Card>

      {/* Editable Fields */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Edit Agent</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="agent-name" className="text-sm font-medium block mb-1">
              Name
            </label>
            <Input
              className="rounded-[10px] bg-white"
              id="agent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Agent name"
            />
          </div>
          <div>
            <label htmlFor="agent-description" className="text-sm font-medium block mb-1">
              Description
            </label>
            <Textarea
              className="rounded-[10px] bg-white"
              id="agent-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Agent description"
              rows={3}
            />
          </div>
          <Button
            className="rounded-full"
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            size="sm"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
